// SAFE MODE: delivery sessions improvements
// External album download page - /album-download/:token
// Verify token, show preview, provide download options (Full / Photos / Videos)

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { verifyAlbumToken, downloadAlbumZip } from '../services/secureAlbumService';
import { getPhotographyTypeLabelAr } from '../utils/bookingLabels';
import Icon from '../components/AppIcon';

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const AlbumDownload = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [files, setFiles] = useState([]);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('الرابط غير صالح');
      setLoading(false);
      return;
    }
    (async () => {
      const result = await verifyAlbumToken(token);
      if (result.ok) {
        setDelivery(result.delivery);
        setFiles(result.files || []);
      } else {
        setError(result.message || 'الرابط منتهي الصلاحية أو غير صالح');
      }
      setLoading(false);
    })();
  }, [token]);

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      const result = await downloadAlbumZip(token, type);
      if (result.ok && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.fileName || 'album.zip';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        alert(result.message || 'فشل التحميل');
      }
    } catch (e) {
      alert('حدث خطأ أثناء التحميل');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري تحميل بيانات الألبوم...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" dir="rtl">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 max-w-md text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">الرابط غير صالح</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <p className="text-sm text-slate-500">الرابط صالح لمدة 15 دقيقة فقط. يرجى العودة للتطبيق وإنشاء رابط جديد.</p>
        </div>
      </div>
    );
  }

  const sessionTitle = getPhotographyTypeLabelAr(delivery?.session_title) || 'جلسة تصوير';
  const sessionDate = formatDate(delivery?.session_date);
  const totalSize = formatFileSize(delivery?.total_size_bytes);
  const photoCount = delivery?.total_photos ?? 0;
  const videoCount = delivery?.total_videos ?? 0;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" dir="rtl">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Session Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h1 className="text-xl font-bold text-slate-900 mb-1">{sessionTitle}</h1>
          <p className="text-slate-500 text-sm mb-4">{sessionDate}</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="flex items-center gap-1 text-slate-600">
              <Icon name="Image" size={16} />
              {photoCount} صور
            </span>
            <span className="flex items-center gap-1 text-slate-600">
              <Icon name="Video" size={16} />
              {videoCount} فيديو
            </span>
            <span className="text-slate-500">الحجم التقريبي: {totalSize}</span>
          </div>
        </div>

        {/* Preview Thumbnails */}
        {files.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4">معاينة</h3>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {files.slice(0, 24).map((f, i) => (
                <div key={f.id || i} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                  {f.thumbnail_url ? (
                    <img src={f.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name={f.file_type === 'video' ? 'Film' : 'Image'} size={20} className="text-slate-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Download Options */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4">خيارات التحميل</h3>
          <div className="space-y-3">
            <button
              onClick={() => handleDownload('full')}
              disabled={!!downloading}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-primary text-white font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {downloading === 'full' ? (
                <>
                  <Icon name="Loader2" size={20} className="animate-spin" />
                  جاري التحميل...
                </>
              ) : (
                <>
                  <Icon name="DownloadCloud" size={20} />
                  تحميل الألبوم كاملاً
                </>
              )}
            </button>
            {photoCount > 0 && (
              <button
                onClick={() => handleDownload('photos')}
                disabled={!!downloading}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-primary text-primary font-medium hover:bg-primary/5 disabled:opacity-50 transition-colors"
              >
                {downloading === 'photos' ? (
                  <>
                    <Icon name="Loader2" size={20} className="animate-spin" />
                    جاري التحميل...
                  </>
                ) : (
                  <>
                    <Icon name="Image" size={20} />
                    تحميل الصور فقط ({photoCount})
                  </>
                )}
              </button>
            )}
            {videoCount > 0 && (
              <button
                onClick={() => handleDownload('videos')}
                disabled={!!downloading}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-primary text-primary font-medium hover:bg-primary/5 disabled:opacity-50 transition-colors"
              >
                {downloading === 'videos' ? (
                  <>
                    <Icon name="Loader2" size={20} className="animate-spin" />
                    جاري التحميل...
                  </>
                ) : (
                  <>
                    <Icon name="Video" size={20} />
                    تحميل الفيديوهات فقط ({videoCount})
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Photographer Rating - PART 3 */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-gradient-to-l from-amber-50 to-white">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Icon name="Star" size={20} className="text-amber-500 fill-amber-500" />
              قيّم المصور
            </h3>
            <p className="text-sm text-slate-500 mt-1">شاركنا رأيك لمساعدة الآخرين</p>
          </div>
          <div className="p-6">
            <p className="text-sm text-slate-600 mb-4">
              يمكنك تقييم المصور من داخل التطبيق بعد إكمال التحميل.
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
            >
              <Icon name="ArrowLeft" size={16} />
              العودة للتطبيق
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlbumDownload;
